// apps/client/src/hooks/useBrokerLoanBooks.ts
import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

export interface BrokerLoanBooksFilters {
  companyId: string;
  officeId?: string;
  status?: 'all' | 'active' | 'inactive' | 'pending';
  entityCode?: string;
}

export interface TemplateSummary {
  templateId: string;
  templateName: string;
  brokerCount: number;
  loanCount: number;
  firstRunDate: string;
  lastRunDate: string;
  totalCommissionCents: number;
}

export interface BrokerTemplateBreakdown {
  templateId: string;
  templateName: string;
  officeId: string;
  officeName: string;
  loanCount: number;
  firstRunDate: string;
  lastRunDate: string;
  upfrontCents: number;
  trailCents: number;
  totalCents: number;
}

export interface BrokerRecord {
  brokerId: string;
  brokerDisplay: string;
  brokerStatus: 'active' | 'inactive' | 'pending' | 'unknown';
  totalLoanCount: number;
  totalUpfrontCents: number;
  totalTrailCents: number;
  totalCommissionCents: number;
  templates: BrokerTemplateBreakdown[];
}

export interface BrokerLoanBooksResponse {
  templateSummaries: TemplateSummary[];
  brokers: BrokerRecord[];
  totals: {
    brokerCount: number;
    loanCount: number;
    totalCommissionCents: number;
  };
}

interface UseBrokerLoanBooksReturn {
  data: BrokerLoanBooksResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useBrokerLoanBooks(filters: BrokerLoanBooksFilters): UseBrokerLoanBooksReturn {
  const [data, setData] = useState<BrokerLoanBooksResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  const refetch = () => setRefetchTrigger((prev) => prev + 1);

  useEffect(() => {
    if (!filters.companyId) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const fn = httpsCallable(functions, 'getBrokerLoanBooks');
        const result = await fn(filters);
        setData(result.data as BrokerLoanBooksResponse);
      } catch (err: any) {
        console.error('[useBrokerLoanBooks] Error:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch broker loan books');
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filters.companyId, filters.officeId, filters.status, filters.entityCode, refetchTrigger]);

  return { data, loading, error, refetch };
}
